import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { EventsEmitter } from '../event-emitter/EventEmitter';
import { BaseUnsubscribeComponent } from '../../shared-new/components/base-unsubscribe/base-unsubscribe.component';

@Component({
  selector: 'app-notification-template',
  templateUrl: './notification-template.component.html',
  standalone: true,
  imports: [CommonModule, FormsModule],
})
export class NotificationTemplateComponent extends BaseUnsubscribeComponent implements OnInit, OnChanges {
  constructor(protected readonly eventEmitter: EventsEmitter) {
    super();
  }

  ngOnInit(): void { }

  ngOnChanges(changes: SimpleChanges): void {
    let shouldReset = false;
    if (changes['inputRecipients']) {
      this.notificationData.recipients = changes['inputRecipients'].currentValue || [];
      shouldReset = true;
    }
    if (changes['inputMessageTemplate']) {
      this.notificationData.messageTemplate = changes['inputMessageTemplate'].currentValue || {};
      shouldReset = true;
    }
    if (shouldReset) {
      this.resetWorkingData();
      this.toggleRecipientsSelectAll();
      if (this.allowGrouping) {
        this.groupMembers();
      }
    }
  }

  @Input() inputRecipients: any[] = [];
  @Input() inputMessageTemplate: any = {};
  @Input() public allowGrouping: boolean = false;
  @Input() public allowDisableSubmit: boolean = false;
  @Input() public isViewingList: boolean = false;
  @Input() public labelSubmitButton: string = 'Confirm';
  @Input() public allChecked: boolean = true;
  @Output() onSubmit: any = new EventEmitter<any>();
  @Output() onCancel: any = new EventEmitter<any>();

  private notificationData: any = { recipients: [], messageTemplate: {} };

  selectAllRecipientsChecked = true;
  public recipients: any[] = [];
  public messageTemplate: any = {};
  public templateSelection: any = { selectAllChecked: true };
  public isPhoneNotExist: boolean = false;
  public isEmailChanged: boolean = false;
  public isSMSChanged: boolean = false;
  public methods: any = {
    groupMembers: this.groupMembers.bind(this),
    getMembers: this.getMembers.bind(this),
    getGroupedClinicNames: this.getGroupedClinicNames.bind(this),
    isGroupFullySelected: this.isGroupFullySelected.bind(this),
    toggleGroupSelection: this.toggleGroupSelection.bind(this),
    toggleSelectAll: this.toggleSelectAll.bind(this),
    toggleSelection: this.toggleSelection.bind(this),
    emailChangedManual: this.emailChangedManual.bind(this),
    smsChangedManual: this.smsChangedManual.bind(this),
  };

  getMembers() {
    return this.recipients;
  }

  toggleSelectAll() {
    this.recipients.forEach((member) => (member.isSelected = this.templateSelection.selectAllChecked));
    this.eventEmitter.emitNotificationSelectionChange(this.recipients);
    if (this.allowDisableSubmit) {
      this.disableSubmit();
    }
  }

  toggleRecipientsSelectAll() {
    this.recipients.forEach((member) => (member.isSelected = this.allChecked));
    this.selectAllRecipientsChecked = this.recipients.every((member) => member.isSelected);
    this.templateSelection.selectAllChecked = this.recipients.every((member) => member.isSelected);
    this.toggleSelectAll();
  }

  toggleSelection() {
    if (this.notificationData.messageTemplate.notificationTypeId == 0) {
      this.recipients = this.recipients.map((vet) => ({ ...vet, isSelected: false }));
    }
    this.templateSelection.selectAllChecked = this.recipients.every((member) => member.isSelected);
    this.eventEmitter.emitNotificationSelectionChange(this.recipients);
    if (this.allowDisableSubmit) {
      this.disableSubmit();
    }
  }

  private disableSubmit() {
    const recipients = (this.recipients as any[])?.filter((x) => x.isSelected);
    const hasAnyEmail = recipients.some((x) => !!x.email);
    const hasAnyPhone = recipients.some((x) => !!x.phone);
    if (this.isEmailChanged == false) {
      this.messageTemplate.isEmail = hasAnyEmail;
    }
    if (this.isSMSChanged == false) {
      this.messageTemplate.isSMS = hasAnyPhone;
    }
  }

  confirm() {
    this.updateToOriginal();
    this.onSubmit.emit();
  }

  cancel() {
    this.resetWorkingData();
    let count = this.getCountForRecipients();
    let total = this.recipients.length;
    if (count != total) {
      this.templateSelection.selectAllChecked = false;
    }
    if (count == total) {
      this.templateSelection.selectAllChecked = true;
    }
    this.onCancel.emit();
  }

  updateToOriginal() {
    this.notificationData.recipients = JSON.parse(JSON.stringify(this.recipients));
    this.notificationData.messageTemplate = JSON.parse(JSON.stringify(this.messageTemplate));
  }

  resetWorkingData() {
    const { recipients = [], messageTemplate = {} } = this.notificationData;
    this.recipients = recipients;
    this.messageTemplate = messageTemplate;
    const hasPhone = this.recipients.some((vet) => !!vet.phone);
    this.isPhoneNotExist = !hasPhone;
    if (this.isPhoneNotExist && this.messageTemplate) {
      this.messageTemplate.isSMS = false;
    }
  }

  getNotificationData() {
    this.updateToOriginal();
    if (this.notificationData.messageTemplate.notificationTypeId == 0) {
      this.recipients = this.recipients.map((vet) => ({ ...vet, isSelected: false }));
    }
    this.notificationData.recipients = this.recipients.filter((x) => x.isSelected == true);
    return this.notificationData;
  }

  getCountForRecipients() {
    if (this.notificationData.messageTemplate.notificationTypeId == 0) {
      return 0;
    }
    const selectedVets = this.recipients.filter((x) => x.isSelected == true);
    const count = selectedVets.length;
    return count;
  }

  getSelectedRecipients() {
    const selectedVets = this.recipients.filter((x) => x.isSelected == true);
    return selectedVets;
  }

  switchViewModeToNotification() {
    this.resetWorkingData();
    this.toggleSelection();
  }

  groupedMembers: { [key: string]: any[] } = {};

  groupMembers() {
    this.groupedMembers = {};
    for (let member of this.recipients) {
      const group = member.memberName;
      if (!this.groupedMembers[group]) {
        this.groupedMembers[group] = [];
      }
      this.groupedMembers[group].push(member);
    }
  }

  getGroupedClinicNames(): string[] {
    return Object.keys(this.groupedMembers);
  }

  toggleGroupSelection(clinic: string, event: Event) {
    const checked = (event.target as HTMLInputElement).checked;
    for (const member of this.groupedMembers[clinic]) {
      member.isSelected = checked;
    }
    this.toggleSelection();
  }

  isGroupFullySelected(clinic: string): boolean {
    const group = this.groupedMembers[clinic];
    return group.length > 0 && group.every((m) => m.isSelected);
  }

  public emailChangedManual() {
    this.isEmailChanged = true;
  }

  public smsChangedManual() {
    this.isSMSChanged = true;
  }
}
